import React, { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Project } from "../interfaces/Project";
import ActionButton from "./ActionButton";
import { ensureProjectMapMarkerIcon, MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from "./projectMapLeaflet";
import { nominatimSearch } from "../api/nominatimGeocode";
import ProjectsAPI from "../api/projects";
import { getDefaultMapCenter, projectHasMapCoords } from "../utils/projectMapCoords";
import "./ProjectGeolocationModalV1.css";

export const PROJECT_RADIUS_MILES_MIN = 0.25;
export const PROJECT_RADIUS_MILES_MAX = 3;

export function clampProjectRadiusMiles(n: number): number {
    const c = Math.min(PROJECT_RADIUS_MILES_MAX, Math.max(PROJECT_RADIUS_MILES_MIN, n));
    return Math.round(c * 100) / 100;
}

function initialRadiusMiles(project: Project): number {
    const r = project.radiusMiles;
    if (typeof r === "number" && Number.isFinite(r) && r > 0) {
        return clampProjectRadiusMiles(r);
    }
    return PROJECT_RADIUS_MILES_MIN;
}

function FitGeofence({ lat, lng, radiusMiles }: { lat: number; lng: number; radiusMiles: number }) {
    const map = useMap();
    useEffect(() => {
        const bounds = L.latLngBounds([[lat, lng]]);
        const milesPerDegLat = 69;
        const milesPerDegLng = 69 * Math.cos((lat * Math.PI) / 180);
        const dLat = radiusMiles / milesPerDegLat;
        const dLng = radiusMiles / milesPerDegLng;
        bounds.extend([lat + dLat, lng + dLng]);
        bounds.extend([lat - dLat, lng - dLng]);
        map.fitBounds(bounds.pad(0.15), { maxZoom: 16 });
    }, [map, lat, lng, radiusMiles]);
    return null;
}

function MapClickToMove({
    enabled,
    onLatLng,
}: {
    enabled: boolean;
    onLatLng: (lat: number, lng: number) => void;
}) {
    useMapEvents({
        click(e) {
            if (!enabled) return;
            onLatLng(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
}

export interface ProjectGeolocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    project: Project;
    canEdit: boolean;
    onSaved: (updated: Project) => void;
    createPopup: (title: string, message: string) => void;
    setLoading: (loading: boolean) => void;
}

const ProjectGeolocationModalV1: React.FC<ProjectGeolocationModalProps> = ({
    isOpen,
    onClose,
    project,
    canEdit,
    onSaved,
    createPopup,
    setLoading,
}) => {
    ensureProjectMapMarkerIcon();

    const [draftLat, setDraftLat] = useState<number>(0);
    const [draftLng, setDraftLng] = useState<number>(0);
    const [draftRadius, setDraftRadius] = useState<number>(PROJECT_RADIUS_MILES_MIN);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchBusy, setSearchBusy] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        if (projectHasMapCoords(project)) {
            setDraftLat(project.latitude as number);
            setDraftLng(project.longitude as number);
        } else if (canEdit) {
            const c = getDefaultMapCenter();
            setDraftLat(c.lat);
            setDraftLng(c.lng);
        } else {
            setDraftLat(NaN);
            setDraftLng(NaN);
        }
        setDraftRadius(initialRadiusMiles(project));
        setSearchQuery("");
    }, [isOpen, project, canEdit]);

    const onMapClick = useCallback((lat: number, lng: number) => {
        setDraftLat(lat);
        setDraftLng(lng);
    }, []);

    const handleSearch = async () => {
        const q = searchQuery.trim();
        if (!q) {
            createPopup("Search", "Enter an address or place to search.");
            return;
        }
        setSearchBusy(true);
        try {
            const result = await nominatimSearch(q);
            if (!result) {
                createPopup("Search", "No results found. Try a different query.");
                return;
            }
            setDraftLat(result.lat);
            setDraftLng(result.lon);
        } catch (err: any) {
            console.error(err);
            createPopup("Search", err?.message || "Geocoding failed. Try again.");
        } finally {
            setSearchBusy(false);
        }
    };

    const handleSave = async () => {
        const lat = draftLat;
        const lng = draftLng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            createPopup("Error", "Invalid coordinates.");
            return;
        }
        const radiusMiles = clampProjectRadiusMiles(draftRadius);
        try {
            setLoading(true);
            const response = await ProjectsAPI.updateProjectGeolocation(project.projectID, {
                latitude: lat,
                longitude: lng,
                radiusMiles,
            });
            onSaved(response.data);
            createPopup("Success", "Project location saved.");
            onClose();
        } catch (error: any) {
            const status = error?.response?.status;
            const msg = error?.response?.data?.message;
            if (status === 403) {
                createPopup("Permission denied", msg || "You are not allowed to edit project geolocation.");
            } else {
                createPopup("Error", msg || "Failed to save project location.");
            }
        } finally {
            setLoading(false);
        }
    };

    const onRadiusInput = (raw: string) => {
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        setDraftRadius(clampProjectRadiusMiles(n));
    };

    if (!isOpen) return null;

    const lat = draftLat;
    const lng = draftLng;
    const coordsOk = Number.isFinite(lat) && Number.isFinite(lng);
    const displayRadius = Math.max(draftRadius, PROJECT_RADIUS_MILES_MIN);

    return (
        <div className="project-geo-modal-overlay" onClick={onClose} role="presentation">
            <div
                className="project-geo-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="project-geo-modal-title"
            >
                <div className="project-geo-modal-header">
                    <h2 id="project-geo-modal-title">
                        {canEdit ? "Edit project location" : "Project map"}
                    </h2>
                </div>
                <div className="project-geo-modal-body">
                    {canEdit && (
                        <div className="project-geo-search">
                            <input
                                type="text"
                                className="project-geo-search-input"
                                placeholder="Search address or place…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") void handleSearch();
                                }}
                                disabled={searchBusy}
                            />
                            <ActionButton
                                title={searchBusy ? "Searching…" : "Search"}
                                onAction={() => void handleSearch()}
                                isEnabled={!searchBusy && searchQuery.trim() !== ""}
                            />
                        </div>
                    )}
                    {canEdit && (
                        <p className="project-geo-hint">
                            Click the map to move the pin, or drag the marker. Results from{" "}
                            <a href="https://nominatim.openstreetmap.org/" target="_blank" rel="noreferrer">
                                Nominatim
                            </a>{" "}
                            (OpenStreetMap). Use per{" "}
                            <a
                                href="https://operations.osmfoundation.org/policies/nominatim/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                usage policy
                            </a>
                            .
                        </p>
                    )}
                    {canEdit && (
                        <div className="project-geo-radius">
                            <label htmlFor="project-geo-radius-input">Radius (miles)</label>
                            <input
                                id="project-geo-radius-input"
                                type="number"
                                min={PROJECT_RADIUS_MILES_MIN}
                                max={PROJECT_RADIUS_MILES_MAX}
                                step={0.01}
                                value={Number.isFinite(draftRadius) ? draftRadius : PROJECT_RADIUS_MILES_MIN}
                                onChange={(e) => onRadiusInput(e.target.value)}
                            />
                            <span className="project-geo-radius-range">
                                {PROJECT_RADIUS_MILES_MIN}–{PROJECT_RADIUS_MILES_MAX} mi, 2 decimal places
                            </span>
                        </div>
                    )}
                    {!coordsOk ? (
                        <p className="project-geo-no-coords">No coordinates to display.</p>
                    ) : (
                        <div className="project-geo-map-wrap">
                            <MapContainer
                                center={[lat, lng]}
                                zoom={14}
                                className="project-geo-map"
                                scrollWheelZoom={true}
                            >
                                <TileLayer
                                    attribution={MAP_TILE_ATTRIBUTION}
                                    url={MAP_TILE_URL}
                                    maxZoom={MAP_TILE_MAX_ZOOM}
                                    referrerPolicy="strict-origin-when-cross-origin"
                                />
                                <FitGeofence lat={lat} lng={lng} radiusMiles={displayRadius} />
                                <MapClickToMove enabled={canEdit} onLatLng={onMapClick} />
                                <Circle
                                    center={[lat, lng]}
                                    radius={displayRadius * 1609.344}
                                    pathOptions={{
                                        color: "#3388ff",
                                        fillColor: "#3388ff",
                                        fillOpacity: 0.15,
                                        weight: 2,
                                    }}
                                />
                                <Marker
                                    position={[lat, lng]}
                                    draggable={canEdit}
                                    eventHandlers={
                                        canEdit
                                            ? {
                                                  dragend: (e) => {
                                                      const p = e.target.getLatLng();
                                                      setDraftLat(p.lat);
                                                      setDraftLng(p.lng);
                                                  },
                                              }
                                            : undefined
                                    }
                                />
                            </MapContainer>
                        </div>
                    )}
                    {coordsOk && (
                        <p className="project-geo-coords">
                            <strong>Latitude:</strong> {lat.toFixed(6)} &nbsp;
                            <strong>Longitude:</strong> {lng.toFixed(6)}
                            {!canEdit && (
                                <>
                                    {" "}
                                    &nbsp;
                                    <strong>Radius:</strong> {displayRadius.toFixed(2)} mi
                                </>
                            )}
                        </p>
                    )}
                </div>
                <div className="project-geo-modal-actions">
                    {canEdit && coordsOk && (
                        <ActionButton title="Save" onAction={() => void handleSave()} isEnabled={true} />
                    )}
                    <ActionButton title="Close" onAction={onClose} isEnabled={true} />
                </div>
            </div>
        </div>
    );
};

export default ProjectGeolocationModalV1;
